import React, { useEffect, useState, useCallback } from "react";
import {
  Button, IconButton, Table, TableBody, TableCell,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Tooltip, Box, Typography, LinearProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { toast } from "react-toastify";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import { displayClientName } from "../../utils/clientName";
import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";

const TIERS = [
  { value: "", label: "Todos" },
  { value: "diamond", label: "Diamante", color: "#7e57c2" },
  { value: "gold", label: "Ouro", color: "#fbc02d" },
  { value: "silver", label: "Prata", color: "#90a4ae" },
  { value: "bronze", label: "Bronze", color: "#a1887f" },
  { value: "risk", label: "Risco", color: "#e57373" }
];

const tierColor = (tier) => (TIERS.find(t => t.value === tier) || {}).color || "#ccc";
const tierLabel = (tier) => (TIERS.find(t => t.value === tier) || {}).label || tier;

const useStyles = makeStyles(theme => ({
  num: { fontVariantNumeric: "tabular-nums" },
  when: { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", color: theme.palette.text.secondary },
  scoreBar: { height: 6, borderRadius: 3, marginTop: 4 },
  positive: { color: theme.palette.success.main, fontVariantNumeric: "tabular-nums" },
  negative: { color: theme.palette.error.main, fontVariantNumeric: "tabular-nums" },
}));

const ClientScores = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState({ tier: "", search: "" });
  const [detail, setDetail] = useState({ open: false, data: null, history: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter.tier) params.tier = filter.tier;
      if (filter.search) params.search = filter.search;
      const { data } = await api.get("/score/clients", { params });
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Erro ao listar scores");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const recalcOne = async (clientId) => {
    try {
      await api.post(`/score/clients/${clientId}/recalc`);
      toast.success("Score recalculado");
      load();
    } catch (err) {
      toast.error("Erro ao recalcular");
    }
  };

  const recalcAll = async () => {
    if (!(await confirm({ title: "Recalcular o score de todos os clientes?", message: "Pode levar alguns minutos e sobrescreve os valores atuais com as regras vigentes.", destructive: false, confirmLabel: "Recalcular" }))) return;
    try {
      const { data } = await api.post("/score/recalc-all");
      toast.success(`Score recalculado para ${data.recalculated} clientes`);
      load();
    } catch (err) {
      toast.error("Erro ao recalcular");
    }
  };

  const normalizeBreakdown = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (_) {
        return [];
      }
    }
    return [];
  };

  const showDetail = async (row) => {
    try {
      const { data } = await api.get(`/score/clients/${row.clientId}`);
      const breakdown = normalizeBreakdown(
        row.breakdown || (data.score && data.score.breakdown) || []
      );
      setDetail({
        open: true,
        data: { ...row, breakdown },
        history: Array.isArray(data.history) ? data.history : []
      });
    } catch (err) {
      toast.error("Erro ao buscar histórico");
    }
  };

  const columns = [
    {
      key: "client",
      label: "Cliente",
      render: row => (
        <Typography variant="subtitle2">
          {displayClientName(row.client, `#${row.clientId}`)}
        </Typography>
      ),
    },
    {
      key: "score",
      label: "Score",
      width: 200,
      render: row => (
        <div>
          <strong className={classes.num}>{Number(row.score).toFixed(2)}</strong>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, Number(row.score)))}
            className={classes.scoreBar}
          />
        </div>
      ),
    },
    {
      key: "tier",
      label: "Tier",
      // A cor do tier é significado, não decoração: mantém o hex da faixa.
      render: row => <StatusChip color={tierColor(row.tier)} label={tierLabel(row.tier)} />,
    },
    {
      key: "recalculatedAt",
      label: "Recalculado em",
      render: row => (
        <span className={classes.when}>
          {row.recalculatedAt ? new Date(row.recalculatedAt).toLocaleString() : "—"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: row => (
        <>
          <Tooltip title="Ver composição e histórico">
            <IconButton size="small" onClick={() => showDetail(row)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Recalcular">
            <IconButton size="small" onClick={() => recalcOne(row.clientId)}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <MainContainer>
      {confirmDialog}
      <PageHeader
        title="Score de Clientes"
        count={list.length}
        subtitle="Pontuação calculada pelas regras de score. Serve para priorizar quem atender e quem está em risco."
        actions={
          <Button variant="outlined" onClick={recalcAll} startIcon={<RefreshIcon />}>
            Recalcular todos
          </Button>
        }
        filters={
          <>
            <TextField
              select label="Tier" value={filter.tier}
              onChange={e => setFilter(f => ({ ...f, tier: e.target.value }))}
              style={{ minWidth: 160 }}
            >
              {TIERS.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <TextField
              label="Buscar cliente" value={filter.search}
              onChange={e => setFilter(f => ({ ...f, search: e.target.value }))}
            />
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={list}
        loading={loading}
        emptyIcon={<StarBorderIcon />}
        emptyTitle="Nenhum score encontrado"
        emptyDescription="Os scores aparecem depois do primeiro cálculo. Use 'Recalcular todos' para gerar a pontuação inicial."
        emptyAction={
          <Button variant="outlined" onClick={recalcAll} startIcon={<RefreshIcon />}>
            Recalcular todos
          </Button>
        }
      />

      <Dialog open={detail.open} onClose={() => setDetail({ open: false, data: null, history: [] })} maxWidth="md" fullWidth>
        <DialogTitle>
          {displayClientName(detail.data?.client, "Cliente")} — Score {Number(detail.data?.score || 0).toFixed(2)}
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom>Composição</Typography>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Fator</TableCell><TableCell>Detalhe</TableCell><TableCell align="right">Pontos</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {(Array.isArray(detail.data?.breakdown) ? detail.data.breakdown : []).map((b, i) => (
                <TableRow key={i}>
                  <TableCell>{b.name}</TableCell>
                  <TableCell>{b.detail || "—"}</TableCell>
                  <TableCell align="right" className={b.points < 0 ? classes.negative : classes.positive}>
                    {Number(b.points).toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
              {!(Array.isArray(detail.data?.breakdown) ? detail.data.breakdown : []).length && (
                <TableRow><TableCell colSpan={3} align="center">Sem composição</TableCell></TableRow>
              )}
            </TableBody>
          </Table>

          <Box mt={3}>
            <Typography variant="subtitle2" gutterBottom>Histórico</Typography>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Quando</TableCell><TableCell>Score</TableCell><TableCell>Tier</TableCell><TableCell>Motivo</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {(Array.isArray(detail.history) ? detail.history : []).map(h => (
                  <TableRow key={h.id}>
                    <TableCell>{new Date(h.snapshotAt).toLocaleString()}</TableCell>
                    <TableCell>{Number(h.score).toFixed(2)}</TableCell>
                    <TableCell>{tierLabel(h.tier)}</TableCell>
                    <TableCell>{h.reason || "—"}</TableCell>
                  </TableRow>
                ))}
                {!(Array.isArray(detail.history) ? detail.history : []).length && (
                  <TableRow><TableCell colSpan={4} align="center">Sem histórico</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail({ open: false, data: null, history: [] })}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default ClientScores;
